// @pta/i18n — közös i18next init helper a FE/FEA/FEB számára.
//
// Használat:
//   import { createI18n } from '@pta/i18n';
//   const i18n = createI18n({ app: 'fe', apiBase: 'https://api.szmk-net.hu' });
//   await i18n.ready; // vagy <Suspense> a React komponensben
//
// A JSON forrásfájlok build-időben bundled-nek (Vite resolveJsonModule-vel).
// A DB-override-ok a BE `/i18n/:language` endpointról jönnek runtime-ban,
// és az i18next `addResourceBundle(..., deep=true, overwrite=true)` hívással
// felülírják a JSON default-okat.

import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

// Common (minden app)
import commonHu from './locales/hu/common.json';
import commonEn from './locales/en/common.json';

// Refdata (minden app — DB name_key feloldás)
import refdataHu from './locales/hu/refdata.json';
import refdataEn from './locales/en/refdata.json';

// App-specifikus (csak az adott app)
import feHu from './locales/hu/fe.json';
import feEn from './locales/en/fe.json';
import feaHu from './locales/hu/fea.json';
import feaEn from './locales/en/fea.json';
import febHu from './locales/hu/feb.json';
import febEn from './locales/en/feb.json';

export type AppType = 'fe' | 'fea' | 'feb';
export type Language = 'hu' | 'en';

export const SUPPORTED_LANGUAGES: readonly Language[] = ['hu', 'en'] as const;
export const DEFAULT_LANGUAGE: Language = 'hu';

export interface CreateI18nOptions {
  app: AppType;
  apiBase?: string;            // pl. https://api.szmk-net.hu — DB-override fetchhez
  initialLanguage?: Language;  // default: localStorage vagy 'hu'
  pollIntervalMs?: number;     // DB-override poll. 0 = kikapcsolva. Default: 5 perc.
}

/**
 * Létrehoz egy i18next instance-t a megadott app számára, betölti a JSON
 * resource-okat, majd (opcionálisan) letölti és merge-eli a DB-override-okat.
 *
 * A visszatérési érték `ready` egy Promise, ami akkor resolve-ol, ha
 * a JSON init kész. A DB-override async tölt, nem blokkolja az UI-t.
 */
export function createI18n(opts: CreateI18nOptions): {
  i18n: I18nInstance;
  ready: Promise<void>;
  setLanguage: (lang: Language) => Promise<void>;
  stopPolling: () => void;
} {
  const i18n = i18next.createInstance();

  const resources = buildResources(opts.app);

  const initialLang = resolveInitialLanguage(opts.initialLanguage);

  const ready = i18n
    .use(initReactI18next)
    .init({
      resources,
      lng: initialLang,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      // A DB name_key-ek pontokat tartalmaznak (pl. inst_type.primary.kindergarten),
      // ezért a keySeparator-t kikapcsoljuk és a kulcsokat flat-ként kezeljük.
      keySeparator: false,
      nsSeparator: ':',
      defaultNS: 'common',
      ns: ['common', 'refdata', opts.app],
      interpolation: { escapeValue: false }, // React már escape-el
      returnNull: false,
    })
    .then(() => {
      persistLanguage(i18n.language as Language);
    });

  // DB-override betöltés + polling.
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  const pollInterval = opts.pollIntervalMs ?? 5 * 60 * 1000;

  const fetchAndMerge = async () => {
    if (!opts.apiBase) return;
    try {
      const lang = i18n.language as Language;
      await fetchAndMergeOverrides(i18n, opts.apiBase, lang);
    } catch (err) {
      // Log, de ne dobjon — a JSON default már működik.
      // eslint-disable-next-line no-console
      console.warn('[i18n] DB override fetch failed:', err);
    }
  };

  ready.then(() => {
    void fetchAndMerge();
    if (pollInterval > 0) {
      pollTimer = setInterval(() => void fetchAndMerge(), pollInterval);
    }
  });

  const setLanguage = async (lang: Language) => {
    await i18n.changeLanguage(lang);
    persistLanguage(lang);
    // Új nyelvhez is kérjük le a DB override-okat.
    await fetchAndMerge();
  };

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  return { i18n, ready, setLanguage, stopPolling };
}

// -------------------------------------------------------------------------
// Belső helper-ek
// -------------------------------------------------------------------------

function buildResources(app: AppType) {
  const appHu = app === 'fe' ? feHu : app === 'fea' ? feaHu : febHu;
  const appEn = app === 'fe' ? feEn : app === 'fea' ? feaEn : febEn;

  return {
    hu: {
      common: commonHu,
      refdata: refdataHu,
      [app]: appHu,
    },
    en: {
      common: commonEn,
      refdata: refdataEn,
      [app]: appEn,
    },
  };
}

const LANG_STORAGE_KEY = 'pta.lang';

function resolveInitialLanguage(explicit?: Language): Language {
  if (explicit) return explicit;
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored && (SUPPORTED_LANGUAGES as readonly string[]).includes(stored)) {
      return stored as Language;
    }
  }
  return DEFAULT_LANGUAGE;
}

function persistLanguage(lang: Language) {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  }
}

/**
 * A BE `/i18n/:language` endpointja namespace-enkénti JSON-t ad vissza,
 * formátum: `{ [namespace]: { [key]: value } }`.
 * Ezt a lekérdezett nyelvhez merge-eljük az i18next store-jába.
 * `deep=true, overwrite=true` — a DB érték felülírja a JSON default-ot.
 */
async function fetchAndMergeOverrides(
  i18n: I18nInstance,
  apiBase: string,
  lang: Language,
): Promise<void> {
  const url = `${apiBase.replace(/\/$/, '')}/i18n/${lang}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`GET ${url} → ${res.status}`);
  }
  const data = (await res.json()) as Record<string, Record<string, string>>;

  for (const [ns, bundle] of Object.entries(data)) {
    if (bundle && typeof bundle === 'object') {
      i18n.addResourceBundle(lang, ns, bundle, true, true);
    }
  }
}
